import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import {
  ChevronRight, Rss, Power, PowerOff, Trash2, FlaskConical, X,
  ExternalLink, CheckCircle2, Clock, EyeOff, Filter, AlertTriangle,
} from "lucide-react";
import { ScreenTitle } from "@/components/ScreenTitle";

const REASON_LABEL = {
  stale: { label: "Trop vieux", icon: Clock, cls: "text-[var(--text-muted)]" },
  already: { label: "Déjà vu", icon: EyeOff, cls: "text-[var(--text-muted)]" },
  no_match: { label: "Aucun mot-clé", icon: Filter, cls: "text-[var(--text-muted)]" },
  dedup_grouped: { label: "Doublon groupé", icon: EyeOff, cls: "text-amber-600" },
  matched: { label: "Match !", icon: CheckCircle2, cls: "text-emerald-600" },
};

function ProbeModal({ feed, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.post(`/politics/feeds/${feed.id}/probe?limit=30`);
        if (!cancelled) setData(r.data);
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.detail || "Impossible de tester ce flux");
      } finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [feed.id]);

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto"
         onClick={onClose} data-testid="probe-modal">
      <div className="relative bg-white max-w-3xl w-full my-8 border border-black shadow-2xl"
           onClick={(e) => e.stopPropagation()}>
        <header className="sticky top-0 bg-white border-b border-black p-4 flex items-start gap-3">
          <FlaskConical className="w-5 h-5 mt-1" strokeWidth={1.5} />
          <div className="flex-1 min-w-0">
            <div className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)]">Test en direct</div>
            <div className="font-serif text-lg leading-tight">{feed.name}</div>
            <div className="text-xs font-mono text-[var(--text-muted)] break-all mt-0.5">{feed.url}</div>
          </div>
          <button onClick={onClose} data-testid="probe-close"
            className="min-h-[36px] px-3 border border-black hover:bg-black hover:text-white">
            <X className="w-4 h-4" />
          </button>
        </header>

        {loading && <div className="p-8 text-center text-sm text-[var(--text-muted)]">Chargement du flux…</div>}
        {error && <div className="p-8 text-center text-[var(--accent-strong)]">Erreur : {error}</div>}

        {data && (
          <div className="p-4">
            {!data.ok && (
              <div className="mb-4 p-3 border-2 border-[var(--accent-strong)] bg-red-50 text-sm">
                Flux illisible : <code className="font-mono">{data.error}</code>
              </div>
            )}
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mb-4 text-[11px] font-mono uppercase tracking-widest">
              <div className="p-2 border border-black bg-black text-white text-center">
                <div className="text-lg">{data.items_read}</div>
                <div>Lus</div>
              </div>
              <div className="p-2 border border-[var(--border-default)] text-center">
                <div className="text-lg text-[var(--text-muted)]">{data.counts.stale}</div>
                <div>Trop vieux</div>
              </div>
              <div className="p-2 border border-[var(--border-default)] text-center">
                <div className="text-lg text-[var(--text-muted)]">{data.counts.already}</div>
                <div>Déjà vus</div>
              </div>
              <div className="p-2 border border-[var(--border-default)] text-center">
                <div className="text-lg text-[var(--text-muted)]">{data.counts.no_match}</div>
                <div>Sans match</div>
              </div>
              <div className="p-2 border border-emerald-600 text-center">
                <div className="text-lg text-emerald-600 font-semibold">{data.counts.matched}</div>
                <div>Matchés</div>
              </div>
            </div>
            <div className="text-xs font-mono text-[var(--text-muted)] mb-3">
              Fraîcheur : {data.max_age_hours}h · Fenêtre dedup : {data.dedup_window_minutes} min
            </div>

            <ul className="divide-y divide-[var(--border-default)] border-y border-[var(--border-default)]"
                data-testid="probe-items">
              {data.items.map((it, i) => {
                const r = REASON_LABEL[it.reason] || { label: it.reason, icon: X, cls: "text-[var(--text-muted)]" };
                const Icon = r.icon;
                return (
                  <li key={i} className="py-2.5 text-sm">
                    <div className="flex items-start gap-2">
                      <Icon className={`w-4 h-4 mt-0.5 flex-shrink-0 ${r.cls}`} strokeWidth={1.75} />
                      <div className="flex-1 min-w-0">
                        <div className={`text-[10px] font-mono uppercase tracking-widest ${r.cls} mb-0.5`}>
                          {r.label}{it.urgent && " · 🔴 URGENT"}
                          {it.published_at && (
                            <span className="text-[var(--text-muted)] ml-2">
                              {new Date(it.published_at).toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}
                            </span>
                          )}
                        </div>
                        <div className="text-sm leading-snug">{it.title}</div>
                        {Object.keys(it.matches || {}).length > 0 && (
                          <div className="mt-1 flex flex-wrap gap-1">
                            {Object.values(it.matches).flat().slice(0, 6).map((m, k) => (
                              <span key={k} className="text-[10px] font-mono px-1.5 py-0.5 bg-black text-white">{m}</span>
                            ))}
                          </div>
                        )}
                        {it.grouped_with && (
                          <div className="text-[10px] font-mono text-amber-700 mt-1">
                            Groupé avec : « {it.grouped_with} »
                          </div>
                        )}
                        {it.url && (
                          <a href={it.url} target="_blank" rel="noreferrer noopener"
                             className="text-[10px] font-mono text-[var(--text-muted)] hover:underline inline-flex items-center gap-1 mt-1">
                            <ExternalLink className="w-3 h-3" /> ouvrir
                          </a>
                        )}
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>

            {data.items.length === 0 && data.ok && (
              <p className="p-6 text-center text-sm text-[var(--text-muted)]">Aucun item dans ce flux.</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default function PoliticsFeedsPage() {
  const [cats, setCats] = useState([]);
  const [feeds, setFeeds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [probing, setProbing] = useState(null);

  const load = async () => {
    try {
      const [c, f] = await Promise.all([
        api.get("/politics/categories"),
        api.get("/politics/feeds"),
      ]);
      setCats(c.data); setFeeds(f.data);
    } catch (e) { /* interceptor */ }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const byCat = useMemo(() => {
    const map = {};
    for (const c of cats) map[c.id] = { cat: c, feeds: [] };
    for (const f of feeds) if (map[f.category_id]) map[f.category_id].feeds.push(f);
    return map;
  }, [cats, feeds]);

  const toggle = async (f) => {
    try { await api.patch(`/politics/feeds/${f.id}`, { active: !f.active }); load(); }
    catch (e) { toast.error("Impossible"); }
  };
  const remove = async (f) => {
    if (!window.confirm(`Supprimer "${f.name}" ?`)) return;
    try { await api.delete(`/politics/feeds/${f.id}`); toast.success("Supprimé"); load(); }
    catch (e) { toast.error("Suppression impossible"); }
  };

  return (
    <div data-testid="politics-feeds-page">
      <ScreenTitle eyebrow={`${feeds.length} flux au total`} title="Flux de veille" />

      {/* Global feeds-in-error banner */}
      {(() => {
        const broken = feeds.filter((f) => f.active && (f.consecutive_errors || 0) >= 3);
        if (broken.length === 0) return null;
        return (
          <div className="mx-5 mb-4 border-2 border-[var(--accent-strong)] bg-red-50 p-3 flex items-start gap-3"
               data-testid="broken-feeds-banner">
            <AlertTriangle className="w-5 h-5 text-[var(--accent-strong)] mt-0.5 flex-shrink-0" strokeWidth={1.75} />
            <div className="flex-1 min-w-0 text-sm">
              <div className="font-mono uppercase tracking-widest text-[11px] text-[var(--accent-strong)] mb-1">
                {broken.length} flux en erreur persistante
              </div>
              <div className="leading-snug">
                {broken.map((f, i) => (
                  <span key={f.id}>
                    {i > 0 && " · "}
                    <b>{f.name}</b> <span className="text-[var(--text-muted)]">({f.consecutive_errors} ticks)</span>
                  </span>
                ))}
              </div>
              <p className="text-xs text-[var(--text-muted)] mt-1">
                Ces sources n&apos;ont rien renvoyé depuis plusieurs passages. Corrige l&apos;URL, désactive-les ou remplace-les.
              </p>
            </div>
          </div>
        );
      })()}

      {loading && <p className="px-5 text-sm text-[var(--text-muted)]">Chargement…</p>}
      {!loading && cats.length === 0 && (
        <div className="px-5 py-10 text-center text-[var(--text-muted)]">
          <Rss className="w-10 h-10 mx-auto mb-2 opacity-40" />
          <p className="text-sm">Aucune catégorie. Crée-en une d&apos;abord depuis l&apos;onglet Catégories.</p>
        </div>
      )}
      <div className="space-y-6 px-5 pb-5">
        {cats.map((c) => {
          const list = (byCat[c.id]?.feeds) || [];
          return (
            <section key={c.id} className="border border-[var(--border-default)] bg-white">
              <header className="p-3 border-b border-[var(--border-default)] flex items-center gap-3">
                <span className="w-3 h-3" style={{ background: c.color }} />
                <h3 className="font-serif text-base flex-1">{c.name}</h3>
                <span className="text-[11px] font-mono uppercase tracking-widest text-[var(--text-muted)]">{list.length} flux</span>
                <Link to={`/veille/categories/${c.id}`}
                  className="min-h-[36px] px-3 flex items-center gap-1 border border-black bg-white hover:bg-black hover:text-white text-[11px] font-mono uppercase tracking-widest">
                  Gérer <ChevronRight className="w-3.5 h-3.5" />
                </Link>
              </header>
              {list.length === 0 ? (
                <p className="p-4 text-sm text-[var(--text-muted)]">Aucun flux dans cette catégorie.</p>
              ) : (
                <ul className="divide-y divide-[var(--border-default)]">
                  {list.map((f) => (
                    <li key={f.id}
                        className={`p-3 flex items-start gap-2 ${(f.consecutive_errors || 0) >= 3 ? "bg-red-50 border-l-4 border-l-[var(--accent-strong)]" : ""}`}
                        data-testid={`feed-row-${f.id}`}>
                      <div className="flex-1 min-w-0">
                        <div className="text-[11px] font-mono uppercase tracking-widest text-[var(--text-muted)] flex items-center gap-2 flex-wrap">
                          <span className={`w-2 h-2 rounded-full ${f.active ? "bg-[var(--status-success)]" : "bg-[var(--text-muted)]"}`} />
                          {f.active ? "actif" : "inactif"}
                          {(f.consecutive_errors || 0) >= 3 && (
                            <span className="inline-flex items-center gap-1 bg-[var(--accent-strong)] text-white px-1.5 py-0.5"
                                  data-testid={`feed-broken-${f.id}`}
                                  title={f.last_error || ""}>
                              <AlertTriangle className="w-3 h-3" />
                              KO · {f.consecutive_errors} ticks
                            </span>
                          )}
                          {(f.consecutive_errors || 0) > 0 && (f.consecutive_errors || 0) < 3 && (
                            <span className="text-amber-600" title={f.last_error || ""}>
                              · erreur ({f.consecutive_errors})
                            </span>
                          )}
                        </div>
                        <div className="font-serif">{f.name}</div>
                        <div className="text-xs font-mono text-[var(--text-muted)] break-all">{f.url}</div>
                        {(f.consecutive_errors || 0) >= 3 && f.last_error && (
                          <div className="text-xs text-[var(--accent-strong)] font-mono mt-1 break-words">
                            {f.last_error}
                          </div>
                        )}
                      </div>
                      <button onClick={() => setProbing(f)}
                        data-testid={`feed-probe-${f.id}`}
                        title="Tester ce flux en direct"
                        className="min-h-[36px] px-2 border border-black bg-black text-white hover:bg-[#222] flex items-center gap-1">
                        <FlaskConical className="w-3.5 h-3.5" />
                        <span className="text-[10px] font-mono uppercase tracking-widest hidden sm:inline">Tester</span>
                      </button>
                      <button onClick={() => toggle(f)} className="min-h-[36px] px-2 border border-[var(--border-default)] hover:border-black">
                        {f.active ? <PowerOff className="w-3.5 h-3.5" /> : <Power className="w-3.5 h-3.5" />}
                      </button>
                      <button onClick={() => remove(f)} className="min-h-[36px] px-2 border border-[var(--border-default)] hover:bg-[var(--accent-strong)] hover:text-white hover:border-[var(--accent-strong)]">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          );
        })}
      </div>

      {probing && <ProbeModal feed={probing} onClose={() => setProbing(null)} />}
    </div>
  );
}
